import React, { useCallback, useEffect } from 'react';
import { Category, DateFragment, EventPageType } from '@graphql/generated';
import { Box, LinkV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { EventCountdown } from '../EventCountdown';
import { Link as RL } from '@react-router';
import { useLayout } from '../../layouts/LayoutProvider';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useTranslation } from 'react-i18next';
import { useTranslation as useTranslationCore } from '@shared/core';
import { StyledRoot, StyledBoxViewDetails, StyledBoxText, StyledBoxArrow, StyledBoxArrowIcon, styles } from './EventLogisticBlock.styles';
import { InlineEditor } from '@shared/components';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body2ToParagraphOverride,
  display1ToLocationOverride,
  display2ToDateOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { useFormattedDate } from '@shared/hooks/useFormattedDate';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';
import { DEFAULT_LANGUAGE } from '@shared/core/i18n/utils';
import { Skeleton } from '@shared/components/Skeleton';
import { useEventLocation } from '@shared/utils/eventLocation';

export interface EventLogisticsBlockProps
  extends Readonly<{
    location?: Nullable<string>;
    eventHandle?: string;
    page?: Object;
    date?: Maybe<DateFragment & { timeString: string; dateString: string; timezone: string }>;
    shouldBreakDate?: boolean;
    hideEventCountdown: boolean;
    showViewDetailsButton?: boolean;
    nextPageSlug?: Maybe<string>;
    onViewDetailsButtonClicked?: () => void;
  }> {}

// The original, non-localized function for getting the event date header
// preserved to ensure backwards compatibility
const _getEventDateHeaderDeprecated = (date: EventLogisticsBlockProps['date'], shouldBreak: boolean) => {
  let dateHeader = date ? date.dateString.replace(date.timeString, '').trim() : null;
  if (shouldBreak && dateHeader) {
    const [dayOfWeek, ...dates] = dateHeader.split(',');
    dateHeader = `${dayOfWeek}\n${dates.join(',')}`;
  }
  return dateHeader;
};

const _getLocalizedEventDateHeader = (date: EventLogisticsBlockProps['date'], shouldBreak: boolean) => {
  const format = shouldBreak ? 'weekday\ndd MMMM yyyy' : 'weekday, dd MMMM yyyy';
  return useFormattedDate(date?.timestamp, format, { titlecase: true, timezone: date?.timezone }) ?? null;
};

const useEventDateHeader = (date: EventLogisticsBlockProps['date'], shouldBreak: boolean) => {
  const { language } = useLocaleContext();

  const deprecatedDateHeader = _getEventDateHeaderDeprecated(date, shouldBreak);
  const localizedDateHeader = _getLocalizedEventDateHeader(date, shouldBreak);

  useEffect(() => {
    if (language === DEFAULT_LANGUAGE && deprecatedDateHeader !== localizedDateHeader) {
      addError('Localized date mismatch', { context: 'useEventDateHeader', deprecatedDateHeader, localizedDateHeader });
    }
  }, [language, deprecatedDateHeader, localizedDateHeader]);

  return localizedDateHeader;
};

const EventLogisticsBlock: React.FC<EventLogisticsBlockProps> = ({
  children,
  page,
  eventHandle,
  location,
  date,
  shouldBreakDate = false,
  hideEventCountdown,
  showViewDetailsButton,
  nextPageSlug,
  onViewDetailsButtonClicked,
  ...restProps
}) => {
  const { t } = useTranslation('guestSiteWelcome');
  const inlineEditorTransFn = useTranslationCore('joykit').t('inlineEditor');

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const { layout } = useLayout();
  const [isMobile] = useResponsive({ values: { xxs: true, xs: true, sm: false } });
  const isAloha = layout === 'aloha';
  const eventDateHeader = useEventDateHeader(date, shouldBreakDate);
  const queryString = useQueryString();

  const { eventLocationDisplayName, isLoadingEventLocation } = useEventLocation(location);

  const handleViewDetailsButton = useCallback(() => {
    onViewDetailsButtonClicked && onViewDetailsButtonClicked();
  }, [onViewDetailsButtonClicked]);

  const handleEditEventDateClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editDate',
        inlineEditData: { date: date?.dateString || '', timeZone: date?.timezone || 'America/Los_Angeles', label: inlineEditorTransFn.eventDate() },
      },
    });
  });

  const handleEditEventLocationClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editLocation', inlineEditData: { location: location || '', label: inlineEditorTransFn.eventLocation() } },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  if (!children && !location && !date) {
    return null;
  }

  return (
    // position relative should prevent the back sprite from being above the z of this box
    <StyledRoot
      __css={styles.root}
      {...restProps}
    >
      <Flex
        flexDirection={'column'}
        textAlign={'center'}
      >
        {eventDateHeader && (
          <InlineEditor
            elementLabel={inlineEditorTransFn.eventDate()}
            actionData={{
              edit: handleEditEventDateClick,
              font: () => handleEditFontClick(Category.DATE),
            }}
            componentName="eventDate"
            pageName={EventPageType.welcome}
            pageSlug="welcome"
            wrapperCSS={{ zIndex: 100 }}
          >
            <GuestSiteTypographyOverride override={display2ToDateOverride}>
              <TextV2
                data-testid={'logistics-date'}
                typographyVariant={'display2'}
                whiteSpace={'pre-wrap'}
              >
                {eventDateHeader}
              </TextV2>
            </GuestSiteTypographyOverride>
          </InlineEditor>
        )}
        {location && (
          <Box marginTop={5}>
            <InlineEditor
              elementLabel={inlineEditorTransFn.eventLocation()}
              actionData={{
                edit: handleEditEventLocationClick,
                font: () => handleEditFontClick(Category.LOCATION),
              }}
              componentName="eventLocation"
              pageName={EventPageType.welcome}
              pageSlug="welcome"
              wrapperCSS={{ zIndex: 100 }}
            >
              <GuestSiteTypographyOverride override={display1ToLocationOverride}>
                {isLoadingEventLocation ? (
                  <Skeleton
                    data-testid={'logistics-location'}
                    placeholder={<Box typographyVariant={'display1'}>&nbsp;</Box>}
                  />
                ) : (
                  <TextV2
                    data-testid={'logistics-location'}
                    typographyVariant={'display1'}
                  >
                    {eventLocationDisplayName}
                  </TextV2>
                )}
              </GuestSiteTypographyOverride>
            </InlineEditor>
          </Box>
        )}
        {!hideEventCountdown && date && (
          <EventCountdown
            data-testid={'logistics-countdown'}
            milliseconds={date.milliseconds}
            timezone={date.timezone}
            marginTop={6}
          />
        )}
      </Flex>
      {children && <>{children}</>}
      {
        /* View Details + Arrow */
        isAloha && !isMobile && nextPageSlug && (
          <StyledBoxViewDetails show={!!showViewDetailsButton}>
            <LinkV2
              onClick={handleViewDetailsButton}
              as={RL}
              to={`/${eventHandle}/${nextPageSlug}${queryString || ''}`}
              color={'inherit'}
              _visited={{ color: 'inherit' }}
              _hover={{ color: 'inherit' }}
              _active={{ color: 'inherit' }}
              textAlign="center"
              textDecoration="none"
            >
              <GuestSiteTypographyOverride override={body2ToParagraphOverride}>
                <StyledBoxText style={{ fontSize: scaleFontSize(pxToRem(15), Category.PARAGRAPH) }}>{t('viewDetails')}</StyledBoxText>
              </GuestSiteTypographyOverride>
              <StyledBoxArrow>
                <StyledBoxArrowIcon />
              </StyledBoxArrow>
            </LinkV2>
          </StyledBoxViewDetails>
        )
      }
    </StyledRoot>
  );
};

EventLogisticsBlock.displayName = 'EventLogisticsBlock';

export { EventLogisticsBlock };
